import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { TrendingUp, FileText, Building, SquareArrowOutUpRight } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET = 10;

  // centro da barra relativo ao container
  let x = (barRect.left - containerRect.left) + (barRect.width / 2);

  const yTop = (barRect.top - containerRect.top) - OFFSET - tipH;   // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;    // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }

  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));

  // como usamos translateX(-50%), o clamp considera meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function InvestmentChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null); // `${index}-value`
  const [tooltipData, setTooltipData] = useState(null);      // { year, value, barRect }
  const [tooltipPos, setTooltipPos] = useState(null);        // { x, y, placeAbove }
  const [isMobile, setIsMobile] = useState(false);

  const scrollRef = useRef(null);
  const innerRefDesktop = useRef(null);
  const innerRefMobile  = useRef(null);
  const tooltipRef      = useRef(null);

  const investmentData = [
    { year: 2010, value: 29.5 }, { year: 2011, value: 28.9 }, { year: 2012, value: 23.0 },
    { year: 2013, value: 19.0 }, { year: 2014, value: 19.5 }, { year: 2015, value: 12.3 },
    { year: 2016, value: 16.1 }, { year: 2017, value: 13.9 }, { year: 2018, value: 13.1 },
    { year: 2019, value: 10.8 }, { year: 2020, value: 10.3 }, { year: 2021, value: 7.1 },
    { year: 2022, value: 6.8 },  { year: 2023, value: 12.8 }, { year: 2024, value: 13.4 }
  ];

  const CHART_HEIGHT = 320;
  const maxValue = Math.max(...investmentData.map(d => d.value));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedData(investmentData), 400);
    return () => clearTimeout(timer);
  }, []);

  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 40);

  // Mostrar tooltip (desktop e mobile) com posicionamento seguro
  const showTooltip = (item, key, targetEl) => {
    const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!container) return;

    const barRect = targetEl.getBoundingClientRect();
    setHoveredBarKey(key);
    setTooltipData({ year: item.year, value: item.value, barRect });
    setTooltipPos(null);

    requestAnimationFrame(() => {
      if (!tooltipRef.current) return;
      const tipW = tooltipRef.current.clientWidth;
      const tipH = tooltipRef.current.clientHeight;
      const containerRect = container.getBoundingClientRect();

      const { x, y, placeAbove } = computeSafeTooltipPosition({
        barRect,
        containerRect,
        tipW,
        tipH,
        // em telas pequenas, preferimos mostrar ABAIXO para não cobrir a barra
        prefAbove: !isMobile
      });
      setTooltipPos({ x, y, placeAbove });
    });
  };

  const handleEnter = (item, key) => (e) => {
    showTooltip(item, key, e.currentTarget);
  };

  const handleLeave = () => {
    setHoveredBarKey(null);
    setTooltipData(null);
    setTooltipPos(null);
  };

  // Handlers por plataforma
  const getBarHandlers = (item, key) => {
    if (isMobile) {
      return {
        onTouchStart: (e) => { e.preventDefault(); showTooltip(item, key, e.currentTarget); },
        onTouchEnd: handleLeave,
      };
    }
    return {
      onPointerEnter: (e) => handleEnter(item, key)(e),
      onPointerMove:  (e) => handleEnter(item, key)(e),
      onPointerLeave: handleLeave,
      onMouseEnter:   (e) => handleEnter(item, key)(e),
      onMouseMove:    (e) => handleEnter(item, key)(e),
      onMouseLeave:   handleLeave,
    };
  };

  // --- mobile layout ---
  const BAR_W_MOBILE = 20;
  const GAP_MOBILE = 10;
  const SLOT_W_MOBILE = BAR_W_MOBILE + GAP_MOBILE;
  const contentWidthMobile = investmentData.length * SLOT_W_MOBILE + 16;

  const barBaseClass = (active) =>
    `rounded-t cursor-pointer transition-all duration-300 ${active ? 'bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'}`;

  return (
    <div className="space-y-8">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            Evolução dos Investimentos Públicos (R$ bi)
          </CardTitle>
        </CardHeader>

        <CardContent className="p-6">
          {isMobile ? (
            // --- MOBILE ---
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef} onScroll={handleLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{ height: CHART_HEIGHT, width: contentWidthMobile, paddingTop: 6 }}
              >
                {investmentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: SLOT_W_MOBILE }}>
                    <div
                      className={barBaseClass(hoveredBarKey === `${index}-value`)}
                      style={{
                        width: BAR_W_MOBILE,
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 40}ms`,
                        transform: hoveredBarKey === `${index}-value` ? 'translateY(-2px)' : 'translateY(0)'
                      }}
                      {...getBarHandlers(item, `${index}-value`)}
                    />
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">
                      R$ {tooltipData.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} bilhões
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // --- DESKTOP ---
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {investmentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div
                      className={barBaseClass(hoveredBarKey === `${index}-value`) + " w-10"}
                      style={{
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 50}ms`,
                        transform: hoveredBarKey === `${index}-value` ? 'translateY(-2px)' : 'translateY(0)'
                      }}
                      {...getBarHandlers(item, `${index}-value`)}
                    />
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">
                    R$ {tooltipData.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} bilhões
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded" /> <span>2010–2014</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded" /> <span>2015–2019</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded" /> <span>2020–2023</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded" /> <span>2024</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Box informativo 2024 (mantido) */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-100">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-blue-900">
            Aplicação dos Investimentos em 2024
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-white/60 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-800">Manutenção (R$ 9,68 bi)</h4>
                <p className="text-sm text-gray-700">72% do total para conservação rotineira e periódica da malha, garantindo trafegabilidade.</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-white/60 rounded-lg">
            <div className="flex items-center gap-3">
              <Building className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-800">Construção (R$ 2,82 bi)</h4>
                <p className="text-sm text-gray-700">Para construção e adequação de rodovias, incluindo 124 km de pavimentação.</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-white/60 rounded-lg">
            <div className="flex items-center gap-3">
              <SquareArrowOutUpRight className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-purple-800">Outras Obras (R$ 1,53 bi)</h4>
                <p className="text-sm text-gray-700">Inclui pontes, contornos, acessos a portos e conservação/recuperação.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
